import React, {
  useState,
  useEffect,
  useRef,
  useId,
  forwardRef,
  useImperativeHandle,
  Ref
} from 'react';
import Image from 'next/image';

import { BaseInputProps, TextInputWidth } from '../shared';

import icon from '@/images/error_icon.svg';
import arrowDown from '@/images/arrow-down.svg';
import whiteCheck from '@/images/multi-check.svg';
import cx from 'classnames';

export interface MultiSelectOption {
  key: string;
  value: string;
}

interface MultiSelectProps extends Omit<BaseInputProps, 'value'> {
  options: MultiSelectOption[];
  handleChange: (value: string[]) => void;
  onFocus?: (isFocused: boolean) => void;
  value: string[];
  className?: string;
  single?: boolean;
  maxSelection?: number;
  search?: boolean;
  variant?: 'default' | 'preferences' | 'stateModal';
}

export const MultiSelect = forwardRef(
  (
    {
      options,
      value,
      type,
      label,
      error,
      handleChange,
      maxSelection,
      onFocus,
      width = TextInputWidth.FULL,
      className = '',
      single = false,
      variant = 'default',
      required
    }: MultiSelectProps,
    ref: Ref<unknown>
  ) => {
    const [isFirstTimeRender, setIsFirstTimeRender] = useState<boolean>(true);
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const [checkedValues, setCheckedValues] = useState<string[]>(value);

    const dropdownRef = useRef<HTMLDivElement>(null);
    const inputId = useId();

    useImperativeHandle(ref, () => ({
      clearValue() {
        setCheckedValues([]);
      },
      updateValues(val: string[]) {
        setCheckedValues(val);
      }
    }));

    useEffect(() => {
      if (isFirstTimeRender) {
        setIsFirstTimeRender(false);
      } else {
        handleChange(checkedValues);
      }
    }, [checkedValues]);

    useEffect(() => {
      function handleClickOutside(e: MouseEvent) {
        // parentElement is the component's root element, and because we want
        // to click the dropdown to close the options list when it is open,
        // we need to extend the validation to the root element, otherwise the
        // dropdown won't close
        if (
          dropdownRef.current &&
          !dropdownRef.current.parentElement?.contains(e.target as Node)
        )
          changeFocus(false);
      }

      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }, [dropdownRef]);

    const handleCheck = (key: string) => {
      const newCheckedValues = [...checkedValues];
      const alreadyCheckedIndex = checkedValues.findIndex((k) => k === key);

      if (alreadyCheckedIndex === -1) newCheckedValues.push(key);
      else newCheckedValues.splice(alreadyCheckedIndex, 1);

      if (maxSelection && newCheckedValues.length > maxSelection) {
        setCheckedValues(
          newCheckedValues.slice(newCheckedValues.length - maxSelection)
        );
      } else {
        setCheckedValues(newCheckedValues);
      }

      if (maxSelection === 1) {
        changeFocus(false);
      }
    };

    const formatValue = () => {
      let displayValue = '';

      if (single) {
        displayValue =
          value[0].length >= 10 ? value[0].slice(0, 10) + '...' : value[0];
      } else {
        displayValue =
          value.slice(0, -1).join(', ') +
          (value.length > 1 ? ' and ' : '') +
          value.slice(-1)[0];

        if (displayValue.length >= 10)
          displayValue = `${value.length} Selected`;
      }

      return displayValue;
    };

    const changeFocus = (focused: boolean) => {
      onFocus?.(focused);
      setIsFocused(focused);
    };

    const renderLabel = () => {
      const defaultLabel = variant === 'preferences' ? '' : label;
      return value.length > 0 ? formatValue() : defaultLabel;
    };

    const handleOptionKeyDown = (
      event: React.KeyboardEvent<HTMLInputElement>,
      optionIndex: number,
      optionValue: string,
      optionsCount: number
    ) => {
      // always close the dropdown when pressing Escape
      if (event.key === 'Escape') {
        cancelEvent(event);
        changeFocus(false);
        dropdownRef.current?.parentElement?.focus();
        return;
      }

      // in the case of Enter or Space, we just (un)check the option, without
      // closing the dropdown
      if (['Enter', 'Space'].includes(event.key)) {
        cancelEvent(event);
        handleCheck(optionValue);
        return;
      }

      // and for Tab, we only close the dropdown if navigating away from the
      // options list, and for that we check the first and last options
      if (event.key === 'Tab') {
        // if is the first option, there're more than 1 option and SHIFT was
        // pressed, then we're leaving the options list, so close it
        const isFirstOptionLeave =
          optionIndex === 0 && optionsCount > 1 && event.shiftKey;

        // if is the last option, and there's only 1 option or SHIFT was not
        // pressed, close it
        const isLastOptionLeave =
          optionIndex === optionsCount - 1 &&
          (!event.shiftKey || optionsCount === 1);

        if (isFirstOptionLeave || isLastOptionLeave) {
          cancelEvent(event);
          changeFocus(false);
          dropdownRef.current?.parentElement?.focus();
        }
      }
    };

    const handleWrapperKeyDown = (
      event: React.KeyboardEvent<HTMLElement>,
      focused: boolean
    ) => {
      // always close the dropdown when pressing Escape
      if (event.key === 'Escape') {
        cancelEvent(event);
        changeFocus(false);
      }
      // always open the dropdown when pressing Enter
      else if (event.key === 'Enter') {
        cancelEvent(event);
        changeFocus(focused);
      }
    };

    const cancelEvent = (event: React.KeyboardEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
    };

    const dropdownListId = `multiselect-list-${inputId}`;

    return (
      <>
        <div
          className={cx('relative', {
            'w-full': !width,
            'mb-2': variant !== 'default' && error,
            'mb-4': variant !== 'default' && !error
          })}
          style={width ? { width: `${width}%` } : undefined}
          data-testid={`multiselect-${type}-${label}`}
          role="combobox"
          aria-label={label}
          aria-expanded={isFocused}
          aria-haspopup="listbox"
          aria-controls={dropdownListId}
          title={label}
          tabIndex={0}
          onKeyDown={(evt) => handleWrapperKeyDown(evt, !isFocused)}
        >
          {variant === 'preferences' && (
            <label
              className={cx(
                'absolute rounded-input-box-shape font-medium transition-all duration-300',
                isFocused || !!value.length ? 'text-[0.8em]' : 'text-[1em]',
                isFocused || !!value.length
                  ? 'top-0 -mt-2 ml-4 translate-y-0 bg-white px-1'
                  : 'top-1/2 -translate-y-1/2 pl-4'
              )}
              htmlFor={inputId}
            >
              {label}
              {required ? '*' : ''}
            </label>
          )}

          <div
            className={cx(
              className,
              'flex-start flex cursor-pointer appearance-none items-center rounded-input-box-shape bg-white px-[15px] py-[3px] leading-[1.5] text-text transition-all duration-300 focus:outline-none',
              'border border-solid',
              {
                'border-hibiscus': !!error,
                'border-teal': !error,
                'h-16': variant === 'preferences',
                'h-8': variant !== 'preferences',
                'border-light-navy bg-[#E6F9F5]':
                  !!value.length && variant === 'default',
                'font-medium text-black':
                  !!value.length && variant === 'preferences'
              }
            )}
            style={{
              width: width ? `${width}%` : undefined,
              backgroundImage: `url(${arrowDown.src})`,
              backgroundPosition: 'calc(100% - 22px) center',
              backgroundSize: '12px',
              backgroundRepeat: 'no-repeat'
            }}
            id={`dropdown-${label}`}
            role="button"
            tabIndex={-1}
            aria-expanded={isFocused}
            onClick={() => changeFocus(!isFocused)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                changeFocus(!isFocused);
              }
            }}
            data-testid="multiselect-dropdown"
          >
            {renderLabel()}
          </div>

          <>
            <div
              className={cx(
                'absolute z-[1] grid overflow-x-auto rounded-[25px] bg-white',
                'h-[20vh] min-w-[10rem] p-[1.65rem]',
                'transition-all duration-300',
                'gap-[10px] gap-x-[3rem]',
                'shadow-[0px_4px_16px_rgba(0,0,0,0.16)]',
                'grid-flow-col grid-rows-[none]',
                'tablet:h-auto tablet:min-w-max tablet:max-w-[23rem]',
                {
                  'visible top-[50px] opacity-100': isFocused,
                  'invisible top-0 opacity-0': !isFocused,
                  'tablet:grid-flow-row': options?.length <= 8,
                  'tablet:grid-flow-col tablet:grid-rows-[repeat(8,1fr)]':
                    options && options.length > 8,
                  'tablet:h-[25vh] tablet:w-full tablet:min-w-[100px] tablet:grid-flow-row tablet:overflow-x-auto':
                    variant === 'stateModal'
                }
              )}
              id={dropdownListId}
              role="listbox"
              ref={dropdownRef}
              data-testid="multiselect-checkboxes-wrapper"
            >
              {!!options.length &&
                options.map(({ key, value: optValue }, index) => {
                  return (
                    <div key={key} className="flex h-[22px] items-center gap-4">
                      <div className="relative">
                        <input
                          className={cx(
                            'relative m-0 flex h-3 w-3 cursor-pointer appearance-none items-center justify-center rounded-[4px] border-2 border-solid !p-2 transition-all duration-300',
                            'disabled:!bg-[rgba(0,0,0,0.05)]',
                            !!checkedValues.find((k) => k === optValue)
                              ? 'border-[#00d9b4] bg-[#00d9b4]'
                              : 'border-[#c6c8ca] bg-white'
                          )}
                          type="checkbox"
                          name={label}
                          id={key}
                          value={optValue}
                          onClick={() => handleCheck(optValue)}
                          checked={!!checkedValues.find((k) => k === optValue)}
                          onChange={(e) => e.preventDefault()}
                          onKeyDown={(e) =>
                            handleOptionKeyDown(
                              e,
                              index,
                              optValue,
                              options.length
                            )
                          }
                          data-testid={`checkbox-${key}`}
                        />
                        {!!checkedValues.find((k) => k === optValue) && (
                          <Image
                            src={whiteCheck.src}
                            alt=""
                            className="pointer-events-none absolute"
                            style={{
                              width: '12px',
                              height: '12px',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)'
                            }}
                            width={12}
                            height={12}
                          />
                        )}
                      </div>
                      <label
                        className="typography-body-1 font-medium leading-[22px] text-black"
                        htmlFor={key}
                      >
                        {optValue}
                      </label>
                    </div>
                  );
                })}
            </div>
          </>
        </div>
        <div
          data-testid={`multiselect-${type}-${label}-error`}
          className={cx(
            'flex items-center overflow-hidden text-hibiscus transition-all duration-300',
            error ? 'mb-4 max-h-[24px] opacity-100' : 'mb-0 max-h-0 opacity-0'
          )}
        >
          <div className="ml-4 mr-2 flex h-full">
            <Image src={icon} alt="Multiselect input error" />
          </div>
          {error || ''}
        </div>
      </>
    );
  }
);
